import axios from "axios";
import { EventEmitter } from "events";
import { createContext, useContext, useState } from "react";

const PartnerContext = createContext();

const PartnerEvents = new EventEmitter();

export const PartnerProvider = ({ children }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });

  const [searchParams, setSearchParams] = useState({
    search: "",
  });

  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const updateSearchParams = (newParams) => {
    setSearchParams((prev) => ({ ...prev, ...newParams }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const createPartner = async (data) => {
    try {
      const response = await axios.post(`/partners`, data);
      const partner = response.data;
      setPartners((prev) => [partner, ...prev]);
      PartnerEvents.emit("partner:created");
      return partner;
    } catch (error) {
      console.error("Error creating partner:", error);
      throw error;
    }
  };

  const getPartners = async () => {
    try {
      setLoading(true);
      const params = {
        ...searchParams,
        page: pagination.page,
        limit: pagination.limit,
      };
      const response = await axios.get(`/partners`, { params });
      const { data, total, totalPages } = response.data;
      setPartners(data);
      setPagination((prev) => ({ ...prev, total, totalPages }));
    } catch (error) {
      console.error("Error fetching partners:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getPartnerById = async (partnerId) => {
    try {
      const response = await axios.get(`/partners/${partnerId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching partner by ID:", error);
      throw error;
    }
  };

  const updatePartner = async (partnerId, updatedData) => {
    try {
      const response = await axios.put(`/partners/${partnerId}`, updatedData);
      const updatedPartner = response.data;
      setPartners((prev) =>
        prev.map((p) => (p.id === updatedPartner.id ? updatedPartner : p))
      );
      PartnerEvents.emit("partner:updated");
      return updatedPartner;
    } catch (error) {
      console.error("Error updating partner:", error);
      throw error;
    }
  };

  const deletePartner = async (partnerId) => {
    try {
      await axios.delete(`/partners/${partnerId}`);
      setPartners((prev) => prev.filter((p) => p.id !== partnerId));
      setPagination((prev) => ({
        ...prev,
        total: prev.total > 0 ? prev.total - 1 : 0,
        totalPages: Math.ceil((prev.total - 1) / prev.limit),
      }));
    } catch (error) {
      console.error("Error deleting partner:", error);
      throw error;
    }
  };

  const activatePartner = async (partnerId) => {
    try {
      const response = await axios.put(`/partners/${partnerId}/activate`);
      const updated = response.data;
      setPartners((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      PartnerEvents.emit("partner:updated");
      return updated;
    } catch (error) {
      console.error("Error activating partner:", error);
      throw error;
    }
  };

  const suspendPartner = async (partnerId) => {
    try {
      const response = await axios.put(`/partners/${partnerId}/suspend`);
      const updated = response.data;
      setPartners((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      PartnerEvents.emit("partner:updated");
      return updated;
    } catch (error) {
      console.error("Error suspending partner:", error);
      throw error;
    }
  };

  const deactivatePartner = async (partnerId) => {
    try {
      const response = await axios.put(`/partners/${partnerId}/deactivate`);
      const updated = response.data;
      setPartners((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      PartnerEvents.emit("partner:updated");
      return updated;
    } catch (error) {
      console.error("Error deactivating partner:", error);
      throw error;
    }
  };

  const disconnectPartner = async (partnerId) => {
    try {
      await axios.post(`/partners/${partnerId}/disconnect`);
    } catch (error) {
      console.error("Error disconnecting partner:", error);
      throw error;
    }
  };

  const resetPartnerPassword = async (partnerId) => {
    try {
      const response = await axios.put(`/partners/${partnerId}/password/reset`);
      return response.data;
    } catch (error) {
      console.error("Error resetting partner password:", error);
      throw error;
    }
  };

  const setPartnerCities = async (partnerId, cityIds) => {
    try {
      const response = await axios.put(`/partners/${partnerId}/cities`, {
        cityIds,
      });
      const updatedPartner = response.data;
      setPartners((prev) =>
        prev.map((p) => (p.id === updatedPartner.id ? updatedPartner : p))
      );
      PartnerEvents.emit("partner:updated");
      return updatedPartner;
    } catch (error) {
      console.error("Error setting partner cities:", error);
      throw error;
    }
  };

  const value = {
    partners,
    loading,
    pagination,
    searchParams,
    createPartner,
    getPartners,
    getPartnerById,
    updatePartner,
    deletePartner,
    activatePartner,
    suspendPartner,
    deactivatePartner,
    disconnectPartner,
    resetPartnerPassword,
    setPartnerCities,
    updatePagination,
    updateSearchParams,
    PartnerEvents,
  };

  return (
    <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>
  );
};

export const usePartnerContext = () => {
  const context = useContext(PartnerContext);
  if (!context) {
    throw new Error(
      "usePartnerContext must be used within a PartnerProvider"
    );
  }
  return context;
};
